import {Group, GroupResourceOption} from '../../../graphql/groups.datatypes';
import {makeStyles} from '@material-ui/styles';
import {useSnackbar} from 'notistack';
import React, {useState} from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  colors,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme
} from '@material-ui/core';
import anyLoading from '../../../utils/anyLoading';
import ResourcesTree from '../../Resources/ResourcesTree/ResourcesTree';
import updateGroup, {UpdateGroupInput} from '../GroupDetails/updateGroup.api';
import deleteGroupResourceOption from './deleteGroupResourceOption.api';
import SearchBar from '../../../components/SearchBar';
import GroupOptionEditModal from './GroupOptionEditModal';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import {checkEditLevel} from '../../../utils/checkEditLevel';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  container: {
    marginTop: theme.spacing(3),
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  card: {},
  saveButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  deleteButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: theme.palette.error.light,
    '&:hover': {
      backgroundColor: theme.palette.error.main
    }
  },
}));

export const GroupOptions = (props: { group: Group, isAdmin: boolean, canEdit: boolean }) => {
  const classes = useStyles();
  const {group, isAdmin, canEdit} = props;
  const {enqueueSnackbar} = useSnackbar();
  const [optionsFilter, setOptionsFilter] = useState('');

  const [sendUpdateGroup, updateData] = updateGroup();
  const [sendDeleteGroupResourceOption, deleteOptionData] = deleteGroupResourceOption();
  const loadingData = anyLoading(updateData, deleteOptionData);

  const handleCreateGroupResourceOption = (resourceOptionId: number) => {
    const sanitisedData: UpdateGroupInput = {
      data: {
        id: group.id,
        options: {
          create: [{
            resource_option_id: resourceOptionId,
            mandatory: false,
            editing_level: 0,
            view_level: 0,
          }],
        }
      }
    };

    sendUpdateGroup({variables: sanitisedData})
        .then(() => {
          enqueueSnackbar('Group Updated.', {variant: 'success'});
        })
        .catch(() => {
          enqueueSnackbar('Group Update Failed!', {variant: 'error'});
        });
  };

  const handleDeleteGroupResourceOption = (groupResourceOptionId: number) => {
    sendDeleteGroupResourceOption({variables: {id: groupResourceOptionId}})
        .then(() => {
          enqueueSnackbar('Group Updated.', {variant: 'success'});
        })
        .catch(() => {
          enqueueSnackbar('Group Update Failed!', {variant: 'error'});
        });
  };

  const [selectedGroupOption, setSelectedGroupOption] = useState<GroupResourceOption | null>(null);
  const handleOpenOptionPopup = (groupOption: GroupResourceOption) => {
    setSelectedGroupOption(groupOption);
  };

  const handleCloseOptionPopup = () => {
    setSelectedGroupOption(null);
  };

  const checkLevel = checkEditLevel();

  return (
      <LoadingOverlay loading={loadingData.loading}>
        <Card className={classes.container}>
          <CardHeader title="Selected Options"/>
          <Divider/>

          <CardContent>
            <SearchBar onSearch={(value: string) => setOptionsFilter(value)}/><br/>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Option Internal Name</TableCell>
                  <TableCell style={{width: 100}}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.options && group.options
                    .filter(groupOption => groupOption.option!.internal_name!.indexOf(optionsFilter) !== -1)
                    .filter(groupOption => checkLevel(group.id, groupOption.view_level!))
                    .map(option => (
                        <TableRow
                            hover
                            key={option.id}
                        >
                          <TableCell>{option.option.internal_name}</TableCell>

                          <TableCell style={{whiteSpace: 'nowrap'}}>
                            <Button style={{minWidth: 'unset'}} onClick={() => handleOpenOptionPopup(option)}
                                    className={classes.saveButton} variant="contained"
                                    disabled={group.locked || !checkLevel(group.id, option.editing_level!)}>
                              edit
                            </Button>
                            {isAdmin && (<Button style={{minWidth: 'unset'}} onClick={() => {
                              handleDeleteGroupResourceOption(option.id)
                            }} className={classes.deleteButton} variant="contained" disabled={group.locked}>
                              delete
                            </Button>)}
                          </TableCell>
                        </TableRow>
                    ))}
              </TableBody>
            </Table>
          </CardContent>
          {isAdmin && (
              <React.Fragment>
                <CardHeader title="Select Options"/>
                <CardContent>
                  <ResourcesTree
                      resources={[group.tour!]}
                      optionsTourId={group.tour!.id}
                      optionsHotelId={group.hotel!.id}
                      selectedOptionIds={group.optionIds}
                      onResourceOptionClick={(option, selected?: boolean) => {
                        if (selected) {
                          handleCreateGroupResourceOption(option.id!);
                        } else {
                          if (group.options) {
                            const item = group.options.find(groupResourceOption => groupResourceOption.resource_option_id === option.id!);
                            if (item) handleDeleteGroupResourceOption(item.id)
                          }
                        }
                      }}
                  />
                </CardContent>
              </React.Fragment>
          )}

          {selectedGroupOption && (
              <GroupOptionEditModal onClose={handleCloseOptionPopup} open={true} groupOption={selectedGroupOption}/>
          )}
        </Card>
      </LoadingOverlay>
  );
};

export default GroupOptions;
